import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { useMyTenant, isAdminRole } from '../../hooks/useMyTenant';
import OtherWorkspacePrompt, { projectWorkspace } from '../clientApprovals/OtherWorkspacePrompt';

interface Stakeholder {
  id: string;
  email: string;
  name: string;
  role: string;
  status: 'PENDING' | 'ACTIVE' | 'REVOKED';
  invitedAt: string;
  acceptedAt: string | null;
  notifyOnChangeOrders: boolean;
  notifyOnMilestones: boolean;
  notifyOnWeeklySummary: boolean;
  notifyOnDeployments: boolean;
  notificationChannel: string;
  // #3185 APPROVE.2 (2b flags; 2c read fields). Optional: a legacy response
  // without them reads as "not an approver, nothing pending, eligible".
  isApprover?: boolean;
  isLeadApprover?: boolean;
  approverIneligibleReason?: 'PARTNER' | 'STUDENT' | null;
  approverConfirmationPending?: boolean;
}

interface Props {
  projectId: string;
  /** The project's own workspace (GET /projects/:id); null until it has loaded. */
  projectTenantId: string | null;
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  PENDING: { bg: 'rgba(245,158,11,0.1)', text: '#d97706' },
  ACTIVE:  { bg: 'rgba(34,197,94,0.1)',  text: '#16a34a' },
  REVOKED: { bg: 'rgba(239,68,68,0.1)',  text: '#dc2626' },
};

// Ruling D6: why a person can't be a client approver. Generic, never which partner.
const INELIGIBLE_COPY: Record<'PARTNER' | 'STUDENT', string> = {
  PARTNER: "Delivery partner people can't be client approvers.",
  STUDENT: "Students can't be client approvers.",
};

export const CONFIRM_APPROVER_COPY = 'Invited by your delivery partner. Confirm before they can approve.';
export const NOT_ELIGIBLE_COPY = "Not eligible to approve. Revoke them to replace them.";

// Ranks that pass requireRole('TENANT_MEMBER') on the stakeholder routes.
const MEMBER_RANK_ROLES = ['TENANT_ADMIN', 'ADMIN', 'FACULTY_SUPERVISOR', 'TENANT_MEMBER', 'STUDENT'];

/**
 * #3185 APPROVE.2 2c — who sees which control, mirroring PR 2b's guards
 * (stakeholders.routes.ts, clientApprovalLead.routes.ts):
 *  - manage: member rank. In an ACADEMIC workspace only a faculty supervisor
 *    or admin rank (assertAcademicClientControl), so a STUDENT sees no invite
 *    or Approver controls (recon §5, ruling 6504404677 item 4).
 *  - confirm: the client's own admin (ruling C/D11): admin rank, and the
 *    server's own verdict on the caller (canConfirmApprovers: not partner
 *    staff, not partner-marked, not awaiting their own confirmation; R1
 *    P3-2). The MANAGED contact's UI is APPROVE.4, so admin rank is required here.
 */
export function stakeholderViewer(t: { role?: string; subscriptionTier?: string; canConfirmApprovers?: boolean } | null) {
  const role = t?.role;
  const memberRank = !!role && MEMBER_RANK_ROLES.includes(role);
  const academicOk = t?.subscriptionTier !== 'ACADEMIC' || role === 'FACULTY_SUPERVISOR' || isAdminRole(role);
  return {
    canManage: memberRank && academicOk,
    canConfirm: isAdminRole(role) && t?.canConfirmApprovers === true,
  };
}

const smallBtn = (color: string): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 6, border: `1px solid ${color}`,
  background: 'transparent', color, fontSize: 12, cursor: 'pointer',
});
const badge = (bg: string, text: string): React.CSSProperties => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 9999, fontSize: 12, fontWeight: 600, background: bg, color: text,
});

export default function StakeholdersTab({ projectId, projectTenantId }: Props) {
  const { myTenant } = useMyTenant();
  // Codex R2 finding 2 (ruling 6537648337 item 2): myTenant is the selected
  // workspace. Its standing applies only to that workspace's projects; for any
  // other (or while unknown) no management or Confirm control is shown.
  const workspace = projectWorkspace(projectTenantId, myTenant?.tenantId);
  const inWorkspace = workspace === 'selected';
  const viewerControls = stakeholderViewer(myTenant);
  const canManage = inWorkspace && viewerControls.canManage;
  const canConfirm = inWorkspace && viewerControls.canConfirm;

  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteAsApprover, setInviteAsApprover] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  // R1 P3-5: only the first load shows "Loading..."; a reload after an action
  // refreshes in place, so the table (and focus) stays.
  const loadStakeholders = useCallback(async (opts: { quiet?: boolean } = {}) => {
    if (!opts.quiet) setLoading(true);
    try {
      const data = await apiFetch<{ stakeholders: Stakeholder[] }>(
        `/projects/${projectId}/stakeholders`,
      );
      setStakeholders(data.stakeholders);
      setLoadError(null);
    } catch (err) {
      setLoadError((err as Error).message);
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadStakeholders(); }, [loadStakeholders]);

  // Every row action reports its failure (a 403, 404 or 409 from 2b's guards)
  // instead of swallowing it, then reloads so the list shows the real state.
  const runAction = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError((err as Error).message);
    }
    await loadStakeholders({ quiet: true });
  };

  const handleInvite = async () => {
    setInviteLoading(true);
    try {
      const data = await apiFetch<{ status: 'sent' | 'failed'; message: string }>(
        `/projects/${projectId}/stakeholders/invite`,
        {
          method: 'POST',
          body: JSON.stringify({ email: inviteEmail, name: inviteName, ...(inviteAsApprover ? { isApprover: true } : {}) }),
        },
      );
      setInviteResult({ ok: data.status === 'sent', message: data.message });
      await loadStakeholders({ quiet: true });
    } catch (err) {
      setInviteResult({ ok: false, message: (err as Error).message });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResend = (id: string) => runAction(() =>
    apiFetch(`/projects/${projectId}/stakeholders/${id}/resend`, { method: 'POST' }));

  // R1 P3-5: the "Cancel?" prompt closes whether or not the cancel succeeds.
  const handleCancelInvite = (id: string) => runAction(async () => {
    try {
      await apiFetch(`/projects/${projectId}/stakeholders/${id}`, { method: 'DELETE' });
    } finally {
      setConfirmCancelId(null);
    }
  });

  const handleRevoke = (id: string) => runAction(() =>
    apiFetch(`/projects/${projectId}/stakeholders/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'REVOKED' }) }));

  const handleSetApprover = (id: string, isApprover: boolean) => runAction(() =>
    apiFetch(`/projects/${projectId}/stakeholders/${id}`, { method: 'PATCH', body: JSON.stringify({ isApprover }) }));

  const handleMakeLead = (id: string) => runAction(() =>
    apiFetch(`/projects/${projectId}/stakeholders/${id}/lead`, { method: 'POST' }));

  const handleConfirmApprover = (id: string) => runAction(() =>
    apiFetch(`/client/projects/${projectId}/stakeholders/${id}/confirm-approver`, { method: 'POST' }));

  // "Make Lead" is offered only while no ACTIVE Lead exists (ruling D12; 2b 409s otherwise).
  const hasActiveLead = stakeholders.some((s) => s.status === 'ACTIVE' && s.isLeadApprover);

  const tableStyle: React.CSSProperties = {
    width: '100%', borderCollapse: 'collapse', fontSize: 14,
  };

  const thStyle: React.CSSProperties = {
    textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)',
    fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const tdStyle: React.CSSProperties = {
    padding: '12px', borderBottom: '1px solid var(--border)',
  };

  const renderApproverCell = (s: Stakeholder) => {
    if (s.status === 'REVOKED') return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
    const isActiveLead = s.status === 'ACTIVE' && s.isLeadApprover === true;
    const pending = s.isApprover === true && s.approverConfirmationPending === true;
    const reason = s.approverIneligibleReason ?? null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* The ACTIVE Lead's Approver flag can't be cleared (2b 409, ruling
              6504404677): no control, only the state. Revoking frees the slot. */}
          {isActiveLead ? (
            <span data-testid={`approver-fixed-${s.id}`} title="Revoke the Lead Approver to free the Lead slot." style={{ fontSize: 13 }}>
              Approver
            </span>
          ) : canManage ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: reason && !s.isApprover ? 'not-allowed' : 'pointer' }}>
              <input
                type="checkbox"
                role="switch"
                aria-label={`Approver: ${s.name}`}
                data-testid={`approver-toggle-${s.id}`}
                checked={s.isApprover === true}
                // An ineligible person can't be made an Approver; one already
                // flagged can still be cleared.
                disabled={reason !== null && s.isApprover !== true}
                onChange={(e) => handleSetApprover(s.id, e.target.checked)}
              />
              Approver
            </label>
          ) : s.isApprover ? (
            <span style={{ fontSize: 13 }}>Approver</span>
          ) : (
            <span style={{ color: 'var(--text-secondary)' }}>-</span>
          )}
          {isActiveLead && (
            <span data-testid={`lead-badge-${s.id}`} style={badge('rgba(59,130,246,0.12)', '#2563eb')}>Lead</span>
          )}
          {pending && (
            <span data-testid={`pending-badge-${s.id}`} style={badge('rgba(245,158,11,0.1)', '#d97706')}>
              Pending client confirmation
            </span>
          )}
        </div>
        {canManage && reason !== null && !s.isApprover && (
          <span data-testid={`ineligible-reason-${s.id}`} style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {INELIGIBLE_COPY[reason]}
          </span>
        )}
        {/* R1 P3-3: flagged earlier, ineligible now (partner-marked, or a
            student): still shown as Approver, but out of the approver set. */}
        {reason !== null && s.isApprover && (
          <span data-testid={`not-eligible-${s.id}`} style={{ fontSize: 12, color: '#dc2626' }}>
            {NOT_ELIGIBLE_COPY}
          </span>
        )}
        {pending && canConfirm && (
          <div data-testid={`confirm-approver-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{CONFIRM_APPROVER_COPY}</span>
            <button onClick={() => handleConfirmApprover(s.id)} style={smallBtn('var(--primary)')}>
              Confirm approver
            </button>
          </div>
        )}
        {canManage && !hasActiveLead && s.status === 'ACTIVE' && s.isApprover && !pending && reason === null && (
          <button data-testid={`make-lead-${s.id}`} onClick={() => handleMakeLead(s.id)} style={smallBtn('var(--primary)')}>
            Make Lead
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Client Stakeholders</h3>
        {canManage && (
          <button
            onClick={() => { setShowInviteModal(true); setInviteResult(null); setInviteEmail(''); setInviteName(''); setInviteAsApprover(false); }}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 13,
            }}
          >
            + Invite Client
          </button>
        )}
      </div>

      {workspace === 'other' && projectTenantId && <OtherWorkspacePrompt projectTenantId={projectTenantId} />}

      {actionError && (
        <div role="alert" style={{
          padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
          background: 'rgba(239,68,68,0.1)', color: '#dc2626',
        }}>
          {actionError}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Loading...</p>
      ) : loadError ? (
        <p role="alert" style={{ color: '#dc2626' }}>Couldn&rsquo;t load stakeholders: {loadError}</p>
      ) : stakeholders.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>No stakeholders invited yet.</p>
      ) : (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Joined</th>
              <th style={thStyle}>Approver</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stakeholders.map(s => {
              const colors = STATUS_COLORS[s.status] ?? STATUS_COLORS.PENDING;
              // Revoking or cancelling an Approver clears the flag: in an
              // ACADEMIC workspace that's a supervisor or admin action (2b).
              const canRemove = canManage || (inWorkspace && !!myTenant && MEMBER_RANK_ROLES.includes(myTenant.role) && !s.isApprover);
              return (
                <tr key={s.id}>
                  <td style={tdStyle}>{s.name}</td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{s.email}</td>
                  <td style={tdStyle}>
                    <span style={badge(colors.bg, colors.text)}>
                      {s.status}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--text-secondary)', fontSize: 13 }}>
                    {s.acceptedAt ? new Date(s.acceptedAt).toLocaleDateString() : '-'}
                  </td>
                  <td style={tdStyle}>{renderApproverCell(s)}</td>
                  <td style={tdStyle}>
                    {s.status === 'ACTIVE' && canRemove && (
                      <button onClick={() => handleRevoke(s.id)} style={smallBtn('#ef4444')}>
                        Revoke
                      </button>
                    )}
                    {s.status === 'PENDING' && inWorkspace && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button onClick={() => handleResend(s.id)} style={smallBtn('var(--primary)')}>
                          Resend
                        </button>
                        {!canRemove ? null : confirmCancelId === s.id ? (
                          <>
                            <span style={{ fontSize: 12, color: '#dc2626' }}>Cancel?</span>
                            <button
                              onClick={() => handleCancelInvite(s.id)}
                              style={{ ...smallBtn('#ef4444'), fontWeight: 600 }}
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirmCancelId(null)}
                              style={{ ...smallBtn('var(--border)'), color: 'var(--text-secondary)' }}
                            >
                              Dismiss
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setConfirmCancelId(s.id)} style={smallBtn('#ef4444')}>
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* Invite modal */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={e => { if (e.target === e.currentTarget) setShowInviteModal(false); }}
        >
          <div style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-xl)', borderRadius: 16, padding: 32, maxWidth: 440, width: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 600 }}>Invite Client Stakeholder</h3>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Name</span>
              <input
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14,
                  border: '1px solid var(--border)', background: 'var(--surface-alt)',
                  color: 'var(--text)', boxSizing: 'border-box',
                }}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 4 }}>Email</span>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 14,
                  border: '1px solid var(--border)', background: 'var(--surface-alt)',
                  color: 'var(--text)', boxSizing: 'border-box',
                }}
              />
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={inviteAsApprover}
                onChange={e => setInviteAsApprover(e.target.checked)}
              />
              Invite as Approver
            </label>

            {inviteResult && (
              <div style={{
                padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 16,
                background: inviteResult.ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                color: inviteResult.ok ? '#16a34a' : '#dc2626',
              }}>
                {inviteResult.message}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{
                  padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
                  background: 'transparent', border: '1px solid var(--border)',
                  color: 'var(--text)', fontWeight: 500, fontSize: 14,
                }}
              >
                Close
              </button>
              <button
                onClick={handleInvite}
                disabled={inviteLoading || !inviteEmail || !inviteName}
                style={{
                  padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 14,
                  opacity: (inviteLoading || !inviteEmail || !inviteName) ? 0.5 : 1,
                }}
              >
                {inviteLoading ? 'Sending...' : 'Send Invitation →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
